import React, { useState, useEffect } from "react";
import "./App.css";
import Store from "./redux/store";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ScrollToTop from "./components/Layout/ScrollToTop";
import axios from "axios";
import { loadUser } from "./redux/actions/user";
import { getAllProducts } from "./redux/actions/product";
import { server } from "./server";

// --- Public / User Pages ---
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import ActivationPage from "./pages/ActivationPage";
import ProductsPage from "./pages/ProductsPage";
import ProductDetailsPage from "./pages/ProductDetailsPage";
import BestSellingPage from "./pages/BestSellingPage";
import CheckoutPage from "./pages/CheckoutPage";
import PaymentPage from "./pages/PaymentPage";
import OrderSuccessPage from "./pages/OrderSuccessPage";
import ProfilePage from "./pages/ProfilePage";
import OrderDetailsPage from "./pages/OrderDetailsPage";
import TrackOrderPage from "./pages/TrackOrderPage";
import UserInbox from "./pages/UserInbox";
import FAQPage from "./pages/FAQPage";
import ContactPage from "./pages/ContactPage";
import BlogPage from "./pages/BlogPage";
import BlogDetailPage from "./pages/BlogDetailPage";
import AboutUsPage from "./pages/AboutUsPage";
import ReviewsPage from "./pages/ReviewsPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import BuyerTermsOfServicePage from "./pages/BuyerTermsOfServicePage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import RefundPolicyPage from "./pages/RefundPolicyPage";
import ShippingPolicyPage from "./pages/ShippingPolicyPage";
import UserForgotPasswordPage from "./pages/UserForgotPasswordPage";
import UserResetPasswordPage from "./pages/UserResetPasswordPage";
import PhonePeSuccessPage from "./pages/PhonePeSuccessPage";
import PhonePeFailedPage from "./pages/PhonePeFailedPage";

// --- Admin Pages ---
import AdminDashboardPage from "./pages/Admin/AdminDashboardPage";
import AdminAnalyticsPage from "./pages/Admin/AdminAnalyticsPage";
import AdminDashboardProducts from "./pages/Admin/AdminDashboardProducts";
import AdminDashboardOrders from "./pages/Admin/AdminDashboardOrders";
import AdminOrderDetailsPage from "./pages/Admin/AdminOrderDetailsPage";
import AdminDashboardUsers from "./pages/Admin/AdminDashboardUsers";
import AdminDashboardSellers from "./pages/Admin/AdminDashboardSellers";
import AdminDashboardPendingProducts from "./pages/Admin/AdminDashboardPendingProducts";
import AdminDashboardPendingSellers from "./pages/Admin/AdminDashboardPendingSellers";
import AdminDashboardEvents from "./pages/Admin/AdminDashboardEvents";
import AdminDashboardWithdraw from "./pages/Admin/AdminDashboardWithdraw";
import AdminDashboardBanner from "./pages/Admin/AdminDashboardBanner";
import AdminDashboardCategories from "./pages/Admin/AdminDashboardCategories";
import AdminDashboardLegalPages from "./pages/Admin/AdminDashboardLegalPages";
import AdminDashboardEmailTemplates from "./pages/Admin/AdminDashboardEmailTemplates";
import AdminReviewsPage from "./pages/Admin/AdminReviewsPage";
import AdminBlogPage from "./pages/Admin/AdminBlogPage";
import AdminSiteSettingsPage from "./pages/Admin/AdminSiteSettingsPage";
import AdminFAQPage from "./pages/Admin/AdminFAQPage";
import AdminVendorApprovalsPage from "./pages/Admin/AdminVendorApprovalsPage";
import AdminVendorPaymentsPage from "./pages/Admin/AdminVendorPaymentsPage";
import AdminServicesPage from "./pages/Admin/AdminServicesPage";
import AdminOfferManagementPage from "./pages/Admin/AdminOfferManagementPage";
import AdminAuctionManagementPage from "./pages/Admin/AdminAuctionManagementPage";
import AdminVerificationPage from "./pages/Admin/AdminVerificationPage";

// --- Buyer-facing Routes ---
import AuctionListPage from "./pages/AuctionListPage";
import AuctionDetailPage from "./pages/AuctionDetailPage";
import ShopCreatePage from "./pages/ShopCreatePage";
import ShopLoginPage from "./pages/ShopLoginPage";
import SellerActivationPage from "./pages/SellerActivationPage";

// --- Vendor Dashboard Pages ---
import ShopDashboardPage from "./pages/Shop/ShopDashboardPage";
import ShopHomePage from "./pages/Shop/ShopHomePage";
import ShopSettingsPage from "./pages/Shop/ShopSettingsPage";
import ShopAllProducts from "./pages/Shop/ShopAllProducts";
import ShopCreateProduct from "./pages/Shop/ShopCreateProduct";
import ShopEditProductPage from "./pages/Shop/ShopEditProductPage";
import ShopAllOrders from "./pages/Shop/ShopAllOrders";
import ShopOrderDetails from "./pages/Shop/ShopOrderDetails";
import ShopAllRefunds from "./pages/Shop/ShopAllRefunds";
import ShopAllCoupouns from "./pages/Shop/ShopAllCoupouns";
import ShopAllEvents from "./pages/Shop/ShopAllEvents";
import ShopCreateEvents from "./pages/Shop/ShopCreateEvents";
import ShopWithDrawMoneyPage from "./pages/Shop/ShopWithDrawMoneyPage";
import ShopInboxPage from "./pages/Shop/ShopInboxPage";
import ShopForgotPasswordPage from "./pages/Shop/ShopForgotPasswordPage";
import ShopResetPasswordPage from "./pages/Shop/ShopResetPasswordPage";
import ShopPreviewPage from "./pages/Shop/ShopPreviewPage";

// --- Route Protections ---
import ProtectedRoute from "./routes/ProtectedRoute";
import ProtectedAdminRoute from "./routes/ProtectedAdminRoute";
import SellerProtectedRoute from "./routes/SellerProtectedRoute";

// --- Stripe ---
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { CurrencyProvider } from "./context/CurrencyContext";

const App = () => {
  const [stripeApikey, setStripeApiKey] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);

  async function getStripeApikey() {
    try {
      const { data } = await axios.get(server + "/payment/stripeapikey", { withCredentials: true });
      setStripeApiKey(data.stripeApikey);
    } catch (error) {
      console.log("Error loading stripe key:", error);
    }
  }

  useEffect(() => {
    const initializeApp = async () => {
      try {
        await Store.dispatch(loadUser());
        Store.dispatch(getAllProducts({ limit: 500 }));
        getStripeApikey();
        setIsInitialized(true);
      } catch (error) {
        console.log("Error initializing app:", error);
        setIsInitialized(true);
      }
    };
    initializeApp();
  }, []);

  if (!isInitialized) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-[#ebe3da]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#086872] mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-[#086872]">Loading...</h2>
          <p className="text-gray-600">Preparing your experience</p>
        </div>
      </div>
    );
  }

  return (
    <CurrencyProvider>
      <BrowserRouter>
        <ScrollToTop />
        {stripeApikey && (
          <Elements stripe={loadStripe(stripeApikey)}>
            <Routes>
              <Route path="/payment" element={<ProtectedRoute><PaymentPage /></ProtectedRoute>} />
            </Routes>
          </Elements>
        )}

        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/sign-up" element={<SignupPage />} />
          <Route path="/activation/:activation_token" element={<ActivationPage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/product/:id" element={<ProductDetailsPage />} />
          <Route path="/best-selling" element={<BestSellingPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/auctions" element={<AuctionListPage />} />
          <Route path="/auction/:id" element={<AuctionDetailPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogDetailPage />} />
          <Route path="/about" element={<AboutUsPage />} />
          <Route path="/reviews" element={<ReviewsPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/buyer-terms" element={<BuyerTermsOfServicePage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/refund" element={<RefundPolicyPage />} />
          <Route path="/shipping" element={<ShippingPolicyPage />} />
          <Route path="/forgot-password" element={<UserForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<UserResetPasswordPage />} />
          <Route path="/phonepe/success" element={<PhonePeSuccessPage />} />
          <Route path="/phonepe/failed" element={<PhonePeFailedPage />} />

          {/* Authenticated User Routes */}
          <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
          <Route path="/order/success" element={<OrderSuccessPage />} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/inbox" element={<ProtectedRoute><UserInbox /></ProtectedRoute>} />
          <Route path="/user/order/:id" element={<ProtectedRoute><OrderDetailsPage /></ProtectedRoute>} />
          <Route path="/user/track/order/:id" element={<ProtectedRoute><TrackOrderPage /></ProtectedRoute>} />

          {/* Admin Routes */}
          <Route path="/admin/dashboard" element={<ProtectedAdminRoute><AdminDashboardPage /></ProtectedAdminRoute>} />
          <Route path="/admin/analytics" element={<ProtectedAdminRoute><AdminAnalyticsPage /></ProtectedAdminRoute>} />
          <Route path="/admin-products" element={<ProtectedAdminRoute><AdminDashboardProducts /></ProtectedAdminRoute>} />
          <Route path="/admin-orders" element={<ProtectedAdminRoute><AdminDashboardOrders /></ProtectedAdminRoute>} />
          <Route path="/admin/order/:id" element={<ProtectedAdminRoute><AdminOrderDetailsPage /></ProtectedAdminRoute>} />
          <Route path="/admin-users" element={<ProtectedAdminRoute><AdminDashboardUsers /></ProtectedAdminRoute>} />
          <Route path="/admin-sellers" element={<ProtectedAdminRoute><AdminDashboardSellers /></ProtectedAdminRoute>} />
          <Route path="/admin-pending-products" element={<ProtectedAdminRoute><AdminDashboardPendingProducts /></ProtectedAdminRoute>} />
          <Route path="/admin-pending-sellers" element={<ProtectedAdminRoute><AdminDashboardPendingSellers /></ProtectedAdminRoute>} />
          <Route path="/admin-events" element={<ProtectedAdminRoute><AdminDashboardEvents /></ProtectedAdminRoute>} />
          <Route path="/admin-withdraw-request" element={<ProtectedAdminRoute><AdminDashboardWithdraw /></ProtectedAdminRoute>} />
          <Route path="/admin-banner" element={<ProtectedAdminRoute><AdminDashboardBanner /></ProtectedAdminRoute>} />
          <Route path="/admin-categories" element={<ProtectedAdminRoute><AdminDashboardCategories /></ProtectedAdminRoute>} />
          <Route path="/admin-legal-pages" element={<ProtectedAdminRoute><AdminDashboardLegalPages /></ProtectedAdminRoute>} />
          <Route path="/admin-email-templates" element={<ProtectedAdminRoute><AdminDashboardEmailTemplates /></ProtectedAdminRoute>} />
          <Route path="/admin-reviews" element={<ProtectedAdminRoute><AdminReviewsPage /></ProtectedAdminRoute>} />
          <Route path="/admin-blog" element={<ProtectedAdminRoute><AdminBlogPage /></ProtectedAdminRoute>} />
          <Route path="/admin-site-settings" element={<ProtectedAdminRoute><AdminSiteSettingsPage /></ProtectedAdminRoute>} />
          <Route path="/admin-faq" element={<ProtectedAdminRoute><AdminFAQPage /></ProtectedAdminRoute>} />
          <Route path="/admin-vendor-approvals" element={<ProtectedAdminRoute><AdminVendorApprovalsPage /></ProtectedAdminRoute>} />
          <Route path="/admin-vendor-payments" element={<ProtectedAdminRoute><AdminVendorPaymentsPage /></ProtectedAdminRoute>} />
          <Route path="/admin-services" element={<ProtectedAdminRoute><AdminServicesPage /></ProtectedAdminRoute>} />
          <Route path="/admin-offer-management" element={<ProtectedAdminRoute><AdminOfferManagementPage /></ProtectedAdminRoute>} />
          <Route path="/admin-auction-management" element={<ProtectedAdminRoute><AdminAuctionManagementPage /></ProtectedAdminRoute>} />
          <Route path="/admin-verification" element={<ProtectedAdminRoute><AdminVerificationPage /></ProtectedAdminRoute>} />

          {/* Vendor Registration & Auth Routes */}
          <Route path="/shop-create" element={<ShopCreatePage />} />
          <Route path="/shop-login" element={<ShopLoginPage />} />
          <Route path="/shop-forgot-password" element={<ShopForgotPasswordPage />} />
          <Route path="/shop-reset-password/:token" element={<ShopResetPasswordPage />} />
          <Route path="/seller/activation/:activation_token" element={<SellerActivationPage />} />
          <Route path="/shop/preview/:id" element={<ShopPreviewPage />} />

          {/* Vendor Dashboard Routes */}
          <Route path="/dashboard" element={<SellerProtectedRoute><ShopDashboardPage /></SellerProtectedRoute>} />
          <Route path="/shop/:id" element={<SellerProtectedRoute><ShopHomePage /></SellerProtectedRoute>} />
          <Route path="/settings" element={<SellerProtectedRoute><ShopSettingsPage /></SellerProtectedRoute>} />
          <Route path="/dashboard-messages" element={<SellerProtectedRoute><ShopInboxPage /></SellerProtectedRoute>} />
          <Route path="/dashboard-products" element={<SellerProtectedRoute><ShopAllProducts /></SellerProtectedRoute>} />
          <Route path="/dashboard-create-product" element={<SellerProtectedRoute><ShopCreateProduct /></SellerProtectedRoute>} />
          <Route path="/dashboard-edit-product/:id" element={<SellerProtectedRoute><ShopEditProductPage /></SellerProtectedRoute>} />
          <Route path="/dashboard-orders" element={<SellerProtectedRoute><ShopAllOrders /></SellerProtectedRoute>} />
          <Route path="/order/:id" element={<SellerProtectedRoute><ShopOrderDetails /></SellerProtectedRoute>} />
          <Route path="/dashboard-refunds" element={<SellerProtectedRoute><ShopAllRefunds /></SellerProtectedRoute>} />
          <Route path="/dashboard-coupouns" element={<SellerProtectedRoute><ShopAllCoupouns /></SellerProtectedRoute>} />
          <Route path="/dashboard-create-event" element={<SellerProtectedRoute><ShopCreateEvents /></SellerProtectedRoute>} />
          <Route path="/dashboard-events" element={<SellerProtectedRoute><ShopAllEvents /></SellerProtectedRoute>} />
          <Route path="/dashboard-withdraw-money" element={<SellerProtectedRoute><ShopWithDrawMoneyPage /></SellerProtectedRoute>} />
        </Routes>

        <ToastContainer
          position="bottom-center"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="dark"
        />
      </BrowserRouter>
    </CurrencyProvider>
  );
};

export default App;
